import { useState, useEffect, useMemo } from "react";
import { Row, DateInput } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import Filters from "byzantine/src/filters";
import { FILTER_CONFIG, FilterChips, FilterType } from "./filterConfig";
import { ActionBar, FilterChip } from "./common";
import styles from "./Filters.module.scss";

const DateFilter = ({
  filters,
  isActive,
  handleSetActive,
  handleSetFilter,
  handleToggleFilter,
}: FilterType) => {
  const { l10n } = useLocalization();
  const [dateRange, setDateRange] = useState<{
    min: string;
    max: string;
  }>({
    min: "",
    max: "",
  });

  const defaultLabel = l10n.getString(FILTER_CONFIG[FilterChips.DATE].labelKey);
  const label = useMemo(() => {
    const { min: minKey, max: maxKey } =
      FILTER_CONFIG[FilterChips.DATE].queryKeys;
    const min = filters[minKey];
    const max = filters[maxKey];

    const formattedMin = min ? Filters.longMonthDayYear(min) : "";
    const formattedMax = max ? Filters.longMonthDayYear(max) : "";

    if (min && max) {
      return `${formattedMin} - ${formattedMax}`;
    }

    if (min) {
      return `After ${formattedMin}`;
    }

    if (max) {
      return `Before ${formattedMax}`;
    }

    return defaultLabel;
  }, [filters]);

  useEffect(() => {
    const { min, max } = FILTER_CONFIG[FilterChips.DATE].queryKeys;
    setDateRange({
      min: filters[min] ?? "",
      max: filters[max] ?? "",
    });
  }, [filters]);

  const handleToggleChip = () => {
    handleSetActive(isActive ? "" : FilterChips.DATE);
  };

  const handleApplyFilter = () => {
    const { min, max } = FILTER_CONFIG[FilterChips.DATE].queryKeys;
    handleSetFilter({
      ...filters,
      [min]: dateRange.min === "" ? undefined : dateRange.min,
      [max]: dateRange.max === "" ? undefined : dateRange.max,
    });
    handleToggleChip();
  };

  const handleRemoveFilter = () => {
    handleToggleFilter(FilterChips.DATE);
  };

  const hasError = new Date(dateRange.min) > new Date(dateRange.max);

  const renderDropdown = () => (
    <>
      <div className={styles.filterDropdownContent}>
        <Row>
          <Row.Item>
            <div className={styles.filterInput}>
              <DateInput
                error={
                  hasError ? l10n.getString("start-date-error") : undefined
                }
                disablePortal
                label={l10n.getString("travel-notice-start-date-input")}
                value={dateRange.min}
                onChange={(value: string) => {
                  setDateRange({
                    ...dateRange,
                    min: value,
                  });
                }}
              />
            </div>
          </Row.Item>
          <Row.Item>
            <div className={styles.filterInput}>
              <DateInput
                minDate={dateRange.min}
                disablePortal
                label={l10n.getString("travel-notice-end-date-input")}
                value={dateRange.max}
                onChange={(value: string) => {
                  setDateRange({
                    ...dateRange,
                    max: value,
                  });
                }}
              />
            </div>
          </Row.Item>
        </Row>
      </div>
      <ActionBar
        handleApplyFilter={handleApplyFilter}
        handleRemoveFilter={handleRemoveFilter}
        disableApplyFilter={hasError}
      />
    </>
  );

  return (
    <FilterChip
      dropdownContent={renderDropdown()}
      isActive={isActive}
      handleToggleChip={handleToggleChip}
      label={label}
      disableUserDismiss={true}
    />
  );
};

export default DateFilter;
